import React, { useState } from "react";
import { ShareNetwork, FacebookLogo, XLogo, LinkedinLogo, EnvelopeSimple, LinkSimple, Check } from "@phosphor-icons/react";

/**
 * "Share this campaign" row for public giving pages. Pure client-side share
 * intents (no tracking, no PII) so donors and visitors can amplify the campaign
 * in one tap — the cheapest, most-trusted reach a nonprofit has.
 */
export default function CampaignShareRow({ url, title, message, accent = "var(--accent)", compact = false, testid = "campaign-share" }) {
    const [copied, setCopied] = useState(false);
    const shareUrl = url || (typeof window !== "undefined" ? window.location.href : "");
    const text = message || `Support ${title || "this campaign"} — every gift makes a difference.`;
    const u = encodeURIComponent(shareUrl);
    const tx = encodeURIComponent(text);

    const targets = [
        { key: "facebook", label: "Facebook", Icon: FacebookLogo, href: `https://www.facebook.com/sharer/sharer.php?u=${u}&quote=${tx}` },
        { key: "x", label: "X", Icon: XLogo, href: `https://twitter.com/intent/tweet?url=${u}&text=${tx}` },
        { key: "linkedin", label: "LinkedIn", Icon: LinkedinLogo, href: `https://www.linkedin.com/sharing/share-offsite/?url=${u}` },
        { key: "email", label: "Email", Icon: EnvelopeSimple, href: `mailto:?subject=${encodeURIComponent(title || "A campaign worth sharing")}&body=${tx}%0A%0A${u}` },
    ];

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch { /* noop */ }
    };

    return (
        <div data-testid={testid}>
            <p className="label-eyebrow flex items-center gap-1.5 mb-2" style={{ color: accent }}>
                <ShareNetwork size={14} weight="bold" /> {compact ? "Spread the word" : "Help spread the word"}
            </p>
            <div className="flex flex-wrap gap-2">
                {targets.map(({ key, label, Icon, href }) => (
                    <a key={key} href={href} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-full border border-border bg-white hover:bg-muted transition-colors"
                        data-testid={`${testid}-${key}`} aria-label={`Share on ${label}`}>
                        <Icon size={16} weight="fill" /> <span className="hidden sm:inline">{label}</span>
                    </a>
                ))}
                <button type="button" onClick={copy}
                    className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-full border border-border bg-white hover:bg-muted transition-colors"
                    data-testid={`${testid}-copy`}>
                    {copied ? <Check size={16} weight="bold" className="text-emerald-600" /> : <LinkSimple size={16} />}
                    <span className="hidden sm:inline">{copied ? "Copied!" : "Copy link"}</span>
                </button>
            </div>
        </div>
    );
}
